import { useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import Routing from "./Routing";

interface MapModalProps {
  show: boolean;
  onHide: () => void;
  lat: number;
  lon: number;
  address: string;
  userLocation?: { lat: number; lon: number };
}

export default function MapModal({ show, onHide, lat, lon, address, userLocation }: MapModalProps) {
  const [mapIsReady, setMapIsReady] = useState(false);
  const position: [number, number] = [lat, lon];

  // Reset loading state when modal closes so it shows next time
  const handleClose = () => {
    setMapIsReady(false);
    onHide();
  };

  const getDistanceKm = () => {
  // 1. Safety check: if either location is missing, return empty
  if (!userLocation || !lat || !lon) return "Calculating...";

  try {
    const R = 6371; // Earth radius in km
    const dLat = ((lat - userLocation.lat) * Math.PI) / 180;
    const dLon = ((lon - userLocation.lon) * Math.PI) / 180;
    
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((userLocation.lat * Math.PI) / 180) *
        Math.cos((lat * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
        
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance.toFixed(2); 
  } catch (err) {
    console.error("Distance calculation error:", err);
    return "N/A";
  }
};

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Location Map</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ position: "relative", height: "400px", width: "100%" }}>
          
          {/* LOADER OVERLAY */}
          {!mapIsReady && (
            <div style={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: "#f8f9fa"
            }}>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading Map...</p>
            </div>
          )}

          {show && (
            <MapContainer
              center={position}
              zoom={15}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
              // This triggers when the map is fully initialized
              whenReady={() => setTimeout(() => setMapIsReady(true), 500)} 
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={position}>
                <Popup>{address}</Popup>
              </Marker>

              {userLocation && (
                <>
                  <Marker position={[userLocation.lat, userLocation.lon]}>
                    <Popup>Your Location</Popup>
                  </Marker>
                  <Routing
                    userLocation={userLocation}
                    targetLocation={{ lat, lon }}
                  />
                </>
              )}
            </MapContainer>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
                 {userLocation && (
                    <p className="mb-0">
                        Distance: <b>{getDistanceKm()} km</b>
                    </p>
                )}
            </div>
            <Button
                variant="primary"
                onClick={() =>
                    window.open(
                    `https://www.google.com/maps/dir/?api=1&origin=${userLocation?.lat},${userLocation?.lon}&destination=${lat},${lon}`,
                    "_blank"
                    )
                }
            >
                Get Directions
            </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
